import { compressToUTF16, decompressFromUTF16 } from 'lz-string';
import {
    ComposerAreaNode,
    ComposerAttributeNode,
    ComposerEcommerceSettings,
    ComposerTemplateNode,
    ComposerTemplateSideRestrictions
} from '../../../configurator-components/composer-editors/attributes/composerAttributesModels';
import { randomCode } from './composerHelpers';

const COMPOSER_LOCAL_DRAFT_VERSION = 1;
const COMPOSER_LOCAL_DRAFT_PREFIX = 'zakeke:configurator-composer:local-draft';

export type ComposerLocalDraft = {
    version: number;
    productID: string;
    baseSnapshotHash: string;
    compressedSnapshot: string;
    updatedAt: string;
};

export type HydratedComposerLocalDraft = {
    attributes: ComposerAttributeNode[];
    templates: ComposerTemplateNode[];
    areas: ComposerAreaNode[];
    designs: Map<string, MPlaza.Design>;
    productQuantity: MPlaza.QuantityRule | null;
    ecommerceSettings: ComposerEcommerceSettings | null;
};

const cloneJSON = <T>(value: T): T => JSON.parse(JSON.stringify(value));

const getStorage = () => {
    try {
        return typeof window === 'undefined' ? null : window.localStorage;
    } catch {
        return null;
    }
};

const hashSnapshot = (snapshot: string) => {
    let hash = 2166136261;
    for (let index = 0; index < snapshot.length; index++) {
        hash ^= snapshot.charCodeAt(index);
        hash = Math.imul(hash, 16777619);
    }
    return (hash >>> 0).toString(36);
};

export const getComposerLocalDraftKey = (productID: string) => `${COMPOSER_LOCAL_DRAFT_PREFIX}:v${COMPOSER_LOCAL_DRAFT_VERSION}:${productID}`;

export const saveComposerLocalDraft = (productID: string, baseSnapshot: string, snapshot: string) => {
    const storage = getStorage();
    if (!storage) return false;

    try {
        const draft: ComposerLocalDraft = {
            version: COMPOSER_LOCAL_DRAFT_VERSION,
            productID,
            baseSnapshotHash: hashSnapshot(baseSnapshot),
            compressedSnapshot: compressToUTF16(snapshot),
            updatedAt: new Date().toISOString()
        };
        storage.setItem(getComposerLocalDraftKey(productID), JSON.stringify(draft));
        return true;
    } catch {
        return false;
    }
};

export const loadComposerLocalDraft = (productID: string): { draft: ComposerLocalDraft; snapshot: string } | null => {
    const storage = getStorage();
    if (!storage) return null;

    try {
        const rawDraft = storage.getItem(getComposerLocalDraftKey(productID));
        if (!rawDraft) return null;
        const draft = JSON.parse(rawDraft) as ComposerLocalDraft;
        if (
            draft.version !== COMPOSER_LOCAL_DRAFT_VERSION ||
            draft.productID !== productID ||
            typeof draft.compressedSnapshot !== 'string' ||
            typeof draft.baseSnapshotHash !== 'string'
        ) {
            storage.removeItem(getComposerLocalDraftKey(productID));
            return null;
        }
        const snapshot = decompressFromUTF16(draft.compressedSnapshot);
        if (!snapshot) {
            storage.removeItem(getComposerLocalDraftKey(productID));
            return null;
        }
        return { draft, snapshot };
    } catch {
        return null;
    }
};

export const clearComposerLocalDraft = (productID: string) => {
    const storage = getStorage();
    if (!storage) return;
    try {
        storage.removeItem(getComposerLocalDraftKey(productID));
    } catch {
        // Storage can be unavailable in private/restricted browser contexts. The server save remains authoritative.
    }
};

export const isComposerLocalDraftBasedOn = (draft: ComposerLocalDraft, serverSnapshot: string) => draft.baseSnapshotHash === hashSnapshot(serverSnapshot);

const hydrateAttribute = (attribute: ComposerAttributeNode): ComposerAttributeNode => ({
    ...attribute,
    brokenValues: new Map(),
    options: (attribute.options ?? []).map((option) => ({
        ...option,
        brokenValues: new Map(),
        actions: (option.actions ?? []).map((action) => ({ ...action, brokenValues: new Map() }))
    }))
});

const repairDuplicateDraftAreaCodes = (attributes: ComposerAttributeNode[]): ComposerAttributeNode[] => {
    // Older Composer builds seeded every personalised-text attribute with TEXT.
    // Keep persisted codes and the first draft occurrence; only repair subsequent
    // unsaved area attributes so an existing local draft can still be saved.
    const reserved = new Set(
        attributes
            .filter((attribute) => attribute.idNumeric > 0)
            .map((attribute) => attribute.code.trim().toLowerCase())
            .filter(Boolean)
    );
    return attributes.map((attribute) => {
        const code = attribute.code.trim().toLowerCase();
        if (!code || !reserved.has(code)) {
            if (code) reserved.add(code);
            return attribute;
        }
        if (attribute.idNumeric > 0 || attribute.attributeType !== MPlaza.AttributeType.CustomizableArea) return attribute;
        let replacement: string;
        do {
            replacement = randomCode('personalisation');
        } while (reserved.has(replacement.toLowerCase()));
        reserved.add(replacement.toLowerCase());
        return { ...attribute, code: replacement };
    });
};

const hydrateTemplate = (template: ComposerTemplateNode): ComposerTemplateNode => ({
    ...template,
    restrictions: new Map<number, ComposerTemplateSideRestrictions>(Array.isArray(template.restrictions) ? template.restrictions : []),
    design: new (MPlaza.Design as any)(cloneJSON(template.design)) as MPlaza.Design
});

const hydrateArea = (area: ComposerAreaNode): ComposerAreaNode => ({
    ...area,
    brokenValues: new Map(),
    originalSide: new (MPlaza.Side as any)(cloneJSON(area.originalSide ?? area.data?.side)) as MPlaza.Side,
    originalMeshSides: ((area.originalMeshSides ?? area.data?.meshSides ?? []) as unknown[]).map(
        (meshSide) => new (MPlaza.ModelSceneMeshSide as any)(cloneJSON(meshSide)) as MPlaza.ModelSceneMeshSide
    )
});

export const hydrateComposerLocalDraft = (snapshot: string): HydratedComposerLocalDraft | null => {
    try {
        const parsed = JSON.parse(snapshot) as {
            attributes?: ComposerAttributeNode[];
            templates?: ComposerTemplateNode[];
            areas?: ComposerAreaNode[];
            designs?: Array<[string, unknown]>;
            productQuantity?: unknown;
            ecommerceSettings?: ComposerEcommerceSettings | null;
        };
        if (!Array.isArray(parsed.attributes) || !Array.isArray(parsed.templates) || !Array.isArray(parsed.areas) || !Array.isArray(parsed.designs)) {
            return null;
        }
        // An entirely uninitialized store can be emitted during a development reload.
        // It is not a deliberate deletion: removing persisted templates retains their
        // tombstones in designs. Never replace a loaded product with this empty shell.
        if (!parsed.attributes.length && !parsed.templates.length && !parsed.areas.length && !parsed.designs.length) return null;

        const templates = Array.from(
            new Map(parsed.templates.map((template) => [template.templateID > 0 ? `saved:${template.templateID}` : `draft:${template.id}`, template])).values()
        ).map(hydrateTemplate);
        const areas = Array.from(new Map(parsed.areas.map((area) => [area.sideID > 0 ? `saved:${area.sideID}` : `draft:${area.id}`, area])).values()).map(
            hydrateArea
        );
        const serializedDesigns = new Map(parsed.designs.filter(([key, design]) => typeof key === 'string' && design && typeof design === 'object'));
        const designs = new Map<string, MPlaza.Design>();
        for (const template of templates) {
            // The product template is shared by all its areas. JSON restores each Map value
            // independently, so use one canonical model or a later save creates/updates it
            // once per area. Prefer a design entry to preserve an in-progress local edit.
            const latestDesign = areas.map((area) => serializedDesigns.get([template.id, area.id].join(','))).find((design) => design !== undefined);
            if (latestDesign) template.design = new (MPlaza.Design as any)(cloneJSON(latestDesign)) as MPlaza.Design;
            for (const area of areas) designs.set([template.id, area.id].join(','), template.design);
        }
        // Removed templates still need their tombstone saved. Do not reintroduce stale
        // template/area keys, but retain these deliberate deletions from the local draft.
        for (const [key, serializedDesign] of serializedDesigns) {
            if (!key.startsWith('deleted:')) continue;
            const deletedDesign = new (MPlaza.Design as any)(cloneJSON(serializedDesign)) as MPlaza.Design;
            if (deletedDesign.getDesignID() > 0 && (deletedDesign as any).get('isDeleted')) designs.set(key, deletedDesign);
        }

        return {
            attributes: repairDuplicateDraftAreaCodes(parsed.attributes.map(hydrateAttribute)),
            templates,
            areas,
            designs,
            productQuantity: parsed.productQuantity ? (new (MPlaza.QuantityRule as any)(cloneJSON(parsed.productQuantity)) as MPlaza.QuantityRule) : null,
            ecommerceSettings: parsed.ecommerceSettings ?? null
        };
    } catch {
        return null;
    }
};
